import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'

import { WorkoutCardInfo } from '@src/components/card/workout-card/workout-card-info/workout-card-info'
import { Form } from '@src/components/form/form'
import { Input } from '@src/elements/input/input'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { Link } from '@src/elements/link/link'
import { Select } from '@src/elements/select/select'
import { OptionGroup } from '@src/elements/select/option-group'
import { Option } from '@src/elements/select/option'
import WorkoutData, { WorkoutType } from 'src/data/workout-data'
import { Button } from '@src/elements/button/button'
import SessionManager from '@src/lib/session_manager'
import { BOTTOM_SCROLL_THRESHOLD, CARDS_ON_BUILDER_PAGE } from '@src/util/constants'
import Member from '@src/data/member'
import { WorkoutService } from '@src/service/workout'
import { ApiCallError } from '@src/lib/api_client'
import { WorkoutFilters } from '@src/state/store'
import { defaultWorkoutFilters } from '@src/state/dafaults'

@Component({
  computed: {
    state () {
      return this.$store.state.content
    },
    workoutFilters () {
      return this.$store.state.workoutFilters
    },
    member () {
      return this.$store.state.selectedMember || this.$store.state.memberProfile
    }
  }
})
export class ActivateWorkouts extends Vue {

  @Prop({ required: false, default: 'custom' })
  protected type: 'custom' | 'home'

  @Prop({ required: false, default: false })
  protected disableCreate: boolean

  @Prop({ required: false, default: true })
  protected allowAllWorkouts: boolean

  protected selectedWorkoutId: number | null

  private filteredWorkouts: WorkoutData[] = []
  private loading: boolean = true
  private workoutFilters: WorkoutFilters

  private service: WorkoutService = new WorkoutService()
  private workouts: WorkoutData[] = []
  private error: ApiCallError | null = null

  // needed for pagination
  private renderedWorkoutCards: WorkoutData[] = []
  private page: number = 0
  private member: Member | null
  private scrollableList

  constructor () {
    super()
  }
  beforeMount () {
    this.$store.dispatch('filterWorkouts', { type: this.allowAllWorkouts ? undefined : 'member-trainer' })
  }

  mounted () {
    this.member ? this.loadWorkouts(this.member.id) : this.loadWorkouts()
    this.scrollableList = this.$refs['workout-list-scrollable']
  }

  setDocumentTitle () {
    // noop
  }

  loadWorkouts (memberId?) {
    this.loading = true
    return this.service.loadWorkouts(memberId)
      .then(workouts => {
        this.workouts = workouts ? workouts.sort(function (a, b) {
          return (b.platformUpdatedAt || b.createdAt).localeCompare(a.platformUpdatedAt || a.createdAt)
        }) : []
        this.scroll()
        this.filter()
        this.renderedWorkoutCards = this.filteredWorkouts.slice(this.page * CARDS_ON_BUILDER_PAGE, (this.page + 1) * CARDS_ON_BUILDER_PAGE)
      })
      .catch((error) => this.error = error)
      .finally(() => this.loading = false)
  }

  setSelectedWorkout (id: number | null, memberId: number | null) {
    this.$forceUpdate()
    this.selectedWorkoutId = id
    this.$store.dispatch('updateActiveTrainingWorkouts', this.type === 'custom' ? { newCustomWorkoutId: id } : { newHomeWorkoutId: id })
    if (memberId) return this.loadWorkouts(memberId)
  }

  filter () {
    const filterByName = (workout: WorkoutData) => {
      if (!this.workoutFilters.name || !this.workoutFilters.name.trim().length) {
        return true
      }
      return workout.name.toLowerCase().includes(this.workoutFilters.name.toLowerCase().trim())
    }

    let filterByType = (workout: WorkoutData) => workout.isWorkoutByType(this.workoutFilters.type, this.type)
    this.filteredWorkouts = this.workouts.filter((w) => filterByName(w) && filterByType(w))
  }

  goToCreateWorkout () {
    this.$store.dispatch('removeNewWorkout')
    this.$store.dispatch('setWorkoutType', WorkoutType.CUSTOM)
    this.$router.push(`${SessionManager.Instance.getBaseRoute()}/workout`)
  }

  @Watch('workoutFilters')
  goToBeginning () {
    this.page = 0
    this.filter()
    this.renderedWorkoutCards = this.filteredWorkouts.slice(this.page * CARDS_ON_BUILDER_PAGE, (this.page + 1) * CARDS_ON_BUILDER_PAGE)
    this.scrollableList.scrollTop = 0
  }

  scroll () {
    this.scrollableList.addEventListener('scroll', this.checkBottomOfPage)
  }

  checkBottomOfPage () {
    let bottomOfWindow = this.scrollableList.scrollTop + this.scrollableList.offsetHeight + BOTTOM_SCROLL_THRESHOLD >= this.scrollableList.scrollHeight

    // if bottom of loaded page, load more
    if (bottomOfWindow) {
      if (this.filteredWorkouts.length > this.renderedWorkoutCards.length) {
        this.page++
        this.renderedWorkoutCards = this.renderedWorkoutCards.concat(this.filteredWorkouts.slice(this.page * CARDS_ON_BUILDER_PAGE, (this.page + 1) * CARDS_ON_BUILDER_PAGE))
      }
    }
  }
  filterByName (value: string) {
    this.$store.dispatch('filterWorkouts', { name: value })
  }

  filterByType (value: string) {
    this.$store.dispatch('filterWorkouts', { type: (value === 'all') ? undefined : value })
  }
  resetFilters () {
    this.$store.dispatch('filterWorkouts', defaultWorkoutFilters())
  }

  renderTypeSelect (h: CreateElement) {
    let typeFilterLabel = this.$t(`PAGE.CONTENT.WORKOUTS.TYPES.${{
      undefined: 'ALL',
      'certified': 'CERTIFIED_WORKOUTS',
      'non-certified': 'NON_CERTIFIED_WORKOUTS',
      'member-trainer': 'MEMBER_WORKOUTS'
    }[`${this.workoutFilters.type}`]}`)
    return <Select filter={true} ref='typeFilter' value={typeFilterLabel}>
          <OptionGroup>
            <Option type='single-option' value={'all'} label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.ALL')}
                    selected={this.workoutFilters.type === undefined} select={this.filterByType}
                    detail={this.workouts.filter((w) => w.isWorkoutByType('', this.type)).length.toString()} />
            <Option type='single-option' value={'certified'} label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.CERTIFIED_WORKOUTS')}
                    selected={this.workoutFilters.type === 'certified'} select={this.filterByType}
                    detail={this.workouts.filter((w) => w.isWorkoutByType('certified', this.type)).length.toString()} />
            <Option type='single-option' value={'non-certified'} label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.NON_CERTIFIED_WORKOUTS')}
                    selected={this.workoutFilters.type === 'non-certified'} select={this.filterByType}
                    detail={this.workouts.filter((w) => w.isWorkoutByType('non-certified', this.type)).length.toString()} />
            <Option type='single-option' value={'member-trainer'} label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.MEMBER_WORKOUTS')}
                    selected={this.workoutFilters.type === 'member-trainer'} select={this.filterByType}
                    detail={this.workouts.filter((w) => w.isWorkoutByType('member-trainer', this.type)).length.toString()} />
          </OptionGroup>
      </Select>
  }

  render (h: CreateElement) {
    let renderedWorkouts = this.renderedWorkoutCards.map(workout => {
      return <WorkoutCardInfo workout={workout} key={workout.id}
                              isSelected={this.selectedWorkoutId ? workout.id === this.selectedWorkoutId : false}
                              updateCallback={this.setSelectedWorkout} type='select' />
    })

    return <div class='workout-wrapper'>
      <Form direction={'row'}>
        <Input class='search' search={true} ref='nameFilter' valueChanged={this.filterByName} placeholder={this.$t('SEARCH_PLACEHOLDER')}
               value={this.workoutFilters.name} />
        {this.allowAllWorkouts && this.renderTypeSelect(h)}
      </Form>
      <div ref='workout-list-scrollable' class='scrollable-list'>
      {this.loading ?
        <Loader />
        :
        this.error ?
          <ErrorScreen title={this.$t('PAGE.CONTENT.WORKOUTS.REQUEST_ERROR')} message={this.$t(this.error.messageKey)}>
            <Link align='center' click={() => {
              this.error = null
              this.member ? this.loadWorkouts(this.member.id) : this.loadWorkouts()
            }}
              label={this.$t('TRY_AGAIN')} variant='colored' />
          </ErrorScreen>
          :
          this.workouts.length ?
            renderedWorkouts.length ?
            <div class='option-list'>
              <transition-group name='fade' appear>
                {renderedWorkouts}
              </transition-group>
              <div key='end-page-message' class='end-page-message'>{this.$root.$t('END_OF_PAGE_REACHED')}</div>
            </div>
              : <ErrorScreen
                message={this.workoutFilters.name ? this.$t('PAGE.CONTENT.WORKOUTS.NOT_FOUND.FILTER.NAME', { query: this.workoutFilters.name }) : this.$t('PAGE.CONTENT.WORKOUTS.NOT_FOUND.FILTER.GENERIC')}>
                {(SessionManager.Instance.isManager() || (this.member && this.member.memberPermissions && this.member.memberPermissions.changeActiveStationWorkout)) ? <Link variant='colored' align='center' click={this.resetFilters} label={this.$t('PAGE.CONTENT.WORKOUTS.RESET_FILTERS')} ref='resetFilters'/> : null}
              </ErrorScreen>

            : <ErrorScreen message={this.$t('PAGE.CONTENT.WORKOUTS.NO_WORKOUTS')}>
              {!this.disableCreate && !SessionManager.Instance.isAssistant() ? <Button type='elevated' class='create-btn' align='right' label={this.$t('NEW')} click={this.goToCreateWorkout} /> : null}
            </ErrorScreen>
      }
      </div>
    </div>
  }

  beforeDestroy () {
    this.scrollableList.removeEventListener('scroll', this.checkBottomOfPage)
  }
}
